import { Client } from '@/pages/user/api';
import { AccountModule } from '@/store/modules/account';
import * as Cache from '@/utils/cache.util';

const apiPrefix = 'portal';

export type SchemeOptionItem = {
  code: string;
  name: string;
};

export type SchemeOptionsResult = {
  industryList: SchemeOptionItem[];
  productList: SchemeOptionItem[];
};

export type SchemePlanItem = {
  sn: string;
  title: string;
  industryCode: string;
  industryName: string;
  productCategoryCode: string;
  productCategoryName: string;
  previewUrl: string;
};

export type SchemePlanListResult = {
  total: number;
  list: SchemePlanItem[];
};

export type SchemePlanQueryReq = {
  industryCode?: string;
  productCategoryCode?: string;
};

export const getSchemeOptions = async (): Promise<SchemeOptionsResult | null> => {
  return await Client.get<SchemeOptionsResult>({
    url: apiPrefix + '/web/scheme/options',
  });
};

export const getSchemePlanList = async (data: SchemePlanQueryReq): Promise<SchemePlanListResult | null> => {
  return await Client.get<SchemePlanListResult>({
    url: apiPrefix + '/web/scheme/plan/list',
    data,
  });
};

const parseFileNameFromDisposition = (disposition: string | null) => {
  if (!disposition) {
    return '';
  }
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const normalMatch = disposition.match(/filename="?([^"]+)"?/i);
  if (normalMatch && normalMatch[1]) {
    return decodeURIComponent(normalMatch[1]);
  }
  return '';
};

const readBlobAsText = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob, 'utf-8');
  });

export const batchDownloadSchemePlans = (planSnList: string[]) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/portal/web/scheme/plan/batch-download', true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Content-Type', 'application/json;charset=utf-8');

    const token = AccountModule.token || Cache.getToken('user');
    if (token) {
      xhr.setRequestHeader('Authorization', token);
    }

    xhr.onload = async () => {
      const contentType = xhr.getResponseHeader('Content-Type') || '';
      if (
        xhr.status >= 200 &&
        xhr.status < 300 &&
        (contentType.indexOf('application/zip') > -1 || contentType.indexOf('application/octet-stream') > -1)
      ) {
        const fileName = parseFileNameFromDisposition(xhr.getResponseHeader('Content-Disposition')) || 'scheme-center.zip';
        const blobUrl = window.URL.createObjectURL(xhr.response);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        resolve();
        return;
      }

      try {
        const text = await readBlobAsText(xhr.response);
        const res = JSON.parse(text);
        reject(new Error((res && (res.msg || res.message)) || '下载失败'));
      } catch (error) {
        reject(new Error('下载失败'));
      }
    };

    xhr.onerror = () => reject(new Error('网络异常'));
    xhr.send(JSON.stringify({ planSnList }));
  });
